import { useMemo, useState } from 'react';
import {
  PhoneMissed,
  Loader2,
  CheckCircle2,
  ListPlus,
  PhoneOutgoing,
  Search,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useMissedCalls, useCreateReturnCallTask, type MissedCall } from '@/hooks/useMissedCalls';
import { useTwilio } from '@/contexts/TwilioContext';

function contactLabel(call: MissedCall) {
  const name = call.contact
    ? `${call.contact.first_name ?? ''} ${call.contact.last_name ?? ''}`.trim()
    : '';
  return name || call.from_e164 || 'Desconhecido';
}

/** Motivo legível do não atendimento */
function missedReason(call: MissedCall): { label: string; hint: string } {
  switch (call.status) {
    case 'no-answer':
      return { label: 'Não atendeu', hint: 'Ninguém atendeu a chamada' };
    case 'busy':
      return { label: 'Ocupado', hint: 'Linha ocupada' };
    case 'failed':
      return { label: 'Falhou', hint: 'Falha na chamada' };
    case 'canceled':
      return { label: 'Cancelada', hint: 'Cliente desligou antes de atender' };
    case 'completed':
      return { label: 'Caixa postal / sem conversa', hint: 'Chamada encerrada sem tempo de conversa' };
    default:
      return { label: call.status || 'Desconhecido', hint: 'Status reportado pela operadora' };
  }
}

function callTime(call: MissedCall) {
  return new Date(call.started_at || call.created_at).getTime();
}

interface MissedCallGroup {
  key: string;
  latest: MissedCall;
  calls: MissedCall[];
  hasReturnTask: boolean;
}

export function MissedCallsPanel() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { data: calls = [], isLoading } = useMissedCalls(7);
  const createTask = useCreateReturnCallTask();
  const { makeCall, isReady } = useTwilio();

  const groups = useMemo<MissedCallGroup[]>(() => {
    const map = new Map<string, MissedCall[]>();
    for (const call of calls) {
      const key = call.contact?.id || call.from_e164 || 'unknown';
      const list = map.get(key);
      if (list) list.push(call);
      else map.set(key, [call]);
    }

    return Array.from(map.entries())
      .map(([key, list]) => {
        const sorted = [...list].sort((a, b) => callTime(b) - callTime(a));
        return {
          key,
          latest: sorted[0],
          calls: sorted,
          hasReturnTask: sorted.some((c) => c.hasReturnTask),
        };
      })
      .sort((a, b) => callTime(b.latest) - callTime(a.latest));
  }, [calls]);

  const pending = groups.filter((g) => !g.hasReturnTask).length;

  const handleCallBack = async (call: MissedCall) => {
    if (!call.from_e164) {
      toast.error('Número de origem indisponível');
      return;
    }
    if (!isReady) {
      toast.error('Telefone não está pronto. Aguarde a conexão.');
      return;
    }
    try {
      await makeCall(call.from_e164, call.contact?.id ?? undefined);
      setOpen(false);
    } catch (e) {
      toast.error('Não foi possível iniciar a ligação');
    }
  };

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const term = search.trim().toLowerCase();
  const digits = term.replace(/\D/g, '');
  const filtered = groups.filter(({ latest }) => {
    if (!term) return true;
    const name = contactLabel(latest).toLowerCase();
    const phones = `${latest.from_e164 ?? ''} ${latest.contact?.primary_phone ?? ''}`.replace(/\D/g, '');
    return name.includes(term) || (digits.length > 0 && phones.includes(digits));
  });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8 rounded-full"
          title="Ligações perdidas"
        >
          <PhoneMissed className="h-4 w-4 text-destructive" />
          {pending > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] leading-4 text-center">
              {pending > 99 ? '99+' : pending}
            </span>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <PhoneMissed className="h-4 w-4 text-destructive" />
            Ligações perdidas
          </SheetTitle>
          <SheetDescription>
            Chamadas recebidas nos últimos 7 dias que não foram atendidas.
          </SheetDescription>
        </SheetHeader>

        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou telefone..."
            className="pl-9 rounded-full"
          />
        </div>

        <ScrollArea className="flex-1 -mx-6 px-6 mt-4">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              {groups.length === 0
                ? 'Nenhuma ligação perdida no período. 🎉'
                : 'Nenhum resultado para a busca.'}
            </p>
          ) : (
            <div className="space-y-2 pb-6">
              {filtered.map((group) => {
                const call = group.latest;
                const reason = missedReason(call);
                const attempts = group.calls.length;
                const isOpen = expanded.has(group.key);

                return (
                  <div key={group.key} className="rounded-xl border bg-card p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{contactLabel(call)}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {call.from_e164} → recebida em {call.to_e164}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Última: {new Date(call.started_at || call.created_at).toLocaleString('pt-BR')}
                        </p>
                        <div className="flex flex-wrap items-center gap-1 mt-1.5">
                          <Badge variant="destructive" className="text-[10px]" title={reason.hint}>
                            {reason.label}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] font-mono">
                            {call.status || 'sem status'}
                          </Badge>
                          {attempts > 1 && (
                            <Badge
                              variant="secondary"
                              className="text-[10px]"
                              title="Tentativas deste contato no período"
                            >
                              {attempts} tentativas
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <Button
                          size="sm"
                          className="rounded-full gap-1"
                          disabled={!call.from_e164}
                          onClick={() => handleCallBack(call)}
                        >
                          <PhoneOutgoing className="h-3.5 w-3.5" />
                          Ligar
                        </Button>

                        {group.hasReturnTask ? (
                          <Badge variant="secondary" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" />
                            Tarefa criada
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full gap-1"
                            disabled={createTask.isPending}
                            onClick={() => createTask.mutate({ call, attempts })}
                          >
                            <ListPlus className="h-3.5 w-3.5" />
                            Tarefa
                          </Button>
                        )}
                      </div>
                    </div>

                    {attempts > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => toggleExpanded(group.key)}
                          className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                          {isOpen ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5" />
                          )}
                          {isOpen ? 'Ocultar tentativas' : `Ver ${attempts} tentativas`}
                        </button>

                        {isOpen && (
                          <ul className="mt-2 space-y-1 border-t pt-2">
                            {group.calls.map((attempt) => (
                              <li
                                key={attempt.id}
                                className="flex items-center justify-between gap-2 text-xs text-muted-foreground"
                              >
                                <span>
                                  {new Date(
                                    attempt.started_at || attempt.created_at
                                  ).toLocaleString('pt-BR')}
                                </span>
                                <span>{missedReason(attempt).label}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
